import React, { FC, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { MaterialPriceList } from 'models/material';
import { useTranslation } from 'react-i18next';
import { downloadMaterialPriceListPdf } from 'components/order-details/main-order-view/document-print-actions/files';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { LoadingButton } from '@mui/lab';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { buildPriceListPdfFileName } from 'utils/file';

type MaterialPriceListsActionsProps = {
    onNewMaterialListClick: () => void;
    materialPriceLists: MaterialPriceList[];
    materialPriceList: MaterialPriceList;
    onSelectedPriceList: (selectedPriceList: string) => void;
};
const MaterialPriceListsActions: FC<MaterialPriceListsActionsProps> = ({
    onNewMaterialListClick,
    materialPriceLists,
    materialPriceList,
    onSelectedPriceList,
}: MaterialPriceListsActionsProps) => {
    const { t: translate } = useTranslation(['common', 'lab-settings']);
    const [selectedPriceList, setSelectedPriceList] = useState('');
    const [spinner, setSpinner] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const handlePriceListChange = (event: SelectChangeEvent<string>) => {
        setSelectedPriceList(event.target.value);
        onSelectedPriceList(event.target.value);
    };
    const getDefaultMaterialPriceListName = () => {
        return materialPriceLists.find((list) => list.isStandardPriceList)?.materialPriceListName;
    };

    const handleMaterialPriceListPdf = async () => {
        try {
            setError(null);
            setSpinner(true);
            await downloadMaterialPriceListPdf(
                buildPriceListPdfFileName(currentOrgVar().name, materialPriceList.materialPriceListName),
                materialPriceList._id as string,
            );
        } catch (error) {
            setError(error as Error);
        } finally {
            setSpinner(false);
        }
    };

    return (
        <Box mb={3} display="flex" alignItems="center">
            {error ? <AlertSnackbar type="error" message={error?.message} /> : undefined}
            <Grid container spacing={1}>
                <Grid item sm={6} xs={12} sx={{ visibility: !materialPriceLists.length ? 'hidden' : 'visible' }}>
                    <FormControl style={{ minWidth: 250 }}>
                        <InputLabel>{translate('lab-settings:PRICE_LISTS')}</InputLabel>
                        <Select
                            value={
                                materialPriceList.materialPriceListName ||
                                getDefaultMaterialPriceListName() ||
                                selectedPriceList
                            }
                            label={translate('lab-settings:PRICE_LISTS')}
                            onChange={handlePriceListChange}
                        >
                            {materialPriceLists.map((materialPriceList) => (
                                <MenuItem key={materialPriceList._id} value={materialPriceList.materialPriceListName}>
                                    {materialPriceList.isStandardPriceList
                                        ? `${materialPriceList.materialPriceListName} (${translate(
                                              'common:DEFAULT',
                                          ).toLowerCase()})`
                                        : materialPriceList.materialPriceListName}{' '}
                                    - {materialPriceList.currency}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item sm={6} xs={12} sx={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>
                    <LoadingButton
                        sx={{ marginRight: 1 }}
                        loading={spinner}
                        disabled={spinner || !materialPriceList._id}
                        onClick={handleMaterialPriceListPdf}
                        startIcon={<PictureAsPdfIcon />}
                    >
                        {translate('common:EXPORT_PDF')}
                    </LoadingButton>
                    <Button variant="contained" onClick={onNewMaterialListClick} startIcon={<AddIcon />}>
                        {translate('lab-settings:NEW_PRICE_LIST')}
                    </Button>
                </Grid>
                {!materialPriceLists.length && (
                    <Grid item xs={12}>
                        <Box mt={2}>
                            <Alert severity="info">{translate('lab-settings:NO_PRICE_LISTS')}</Alert>
                        </Box>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default MaterialPriceListsActions;
